import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";

const NUM_FIELDS = ["work_price_low", "work_price_high", "base_price_monthly_low", "base_price_monthly_high"];
const STATUS_LABELS = { new: "Neu", in_progress: "In Bearbeitung", done: "Erledigt", rejected: "Abgelehnt" };
const STATUS_OPTIONS = ["new", "in_progress", "done", "rejected"];

export default function Admin() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [view, setView] = useState("orientation");
  const [ranges, setRanges] = useState([]);
  const [rows, setRows] = useState({}); // id -> editable values
  const [audit, setAudit] = useState([]);
  const [leads, setLeads] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [msg, setMsg] = useState({ text: "", type: "" });
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({ product: "", status: "", from: "", to: "", q: "" });

  const logout = useCallback(() => {
    localStorage.removeItem("checkin_admin_token");
    navigate("/admin/login");
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      const [meRes, rangeRes, auditRes, leadRes, cbRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/admin/orientation"),
        api.get("/admin/audit"),
        api.get("/admin/leads"),
        api.get("/admin/callbacks"),
      ]);
      setMe(meRes.data);
      setRanges(rangeRes.data);
      setAudit(auditRes.data);
      setLeads(leadRes.data);
      setCallbacks(cbRes.data);
      const editable = {};
      rangeRes.data.forEach((r) => {
        const src = r.draft || r.published || {};
        editable[r.id] = {
          postal_code_from: r.postal_code_from,
          postal_code_to: r.postal_code_to,
          work_price_low: src.work_price_low, work_price_high: src.work_price_high,
          base_price_monthly_low: src.base_price_monthly_low, base_price_monthly_high: src.base_price_monthly_high,
          label: src.label || "", valid_from: src.valid_from || "", valid_until: src.valid_until || "",
          hasDraft: !!r.draft,
        };
      });
      setRows(editable);
    } catch (err) {
      if (err.response?.status === 401) logout();
      else setMsg({ text: formatApiErrorDetail(err.response?.data?.detail) || err.message, type: "error" });
    }
  }, [logout]);

  useEffect(() => {
    if (!localStorage.getItem("checkin_admin_token")) { navigate("/admin/login"); return; }
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    load();
    return () => { document.head.removeChild(m); };
  }, [load, navigate]);

  const update = (id, field, value) => setRows((r) => ({ ...r, [id]: { ...r[id], [field]: value } }));

  const buildPayload = () => ranges.map((r) => {
    const e = rows[r.id];
    return {
      id: r.id,
      postal_code_from: e.postal_code_from, postal_code_to: e.postal_code_to,
      work_price_low: Number(e.work_price_low), work_price_high: Number(e.work_price_high),
      base_price_monthly_low: Number(e.base_price_monthly_low), base_price_monthly_high: Number(e.base_price_monthly_high),
      label: e.label, valid_from: e.valid_from, valid_until: e.valid_until || null,
    };
  });

  const save = async (action) => {
    setBusy(true);
    setMsg({ text: "", type: "" });
    try {
      await api.post("/admin/orientation/save", { action, ranges: buildPayload() });
      setMsg({
        text: action === "publish"
          ? "Werte wurden veröffentlicht und sind jetzt im Pre-Check aktiv."
          : "Entwurf gespeichert. Erst nach Veröffentlichung im Pre-Check aktiv.",
        type: "ok",
      });
      await load();
    } catch (err) {
      if (err.response?.status === 401) logout();
      else setMsg({ text: formatApiErrorDetail(err.response?.data?.detail) || err.message, type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const byProduct = (p) => ranges.filter((r) => r.product === p);

  const changeStatus = async (kind, id, status) => {
    try {
      await api.patch(`/admin/${kind}/${id}`, { status });
      if (kind === "leads") setLeads((l) => l.map((x) => (x.id === id ? { ...x, status } : x)));
      else setCallbacks((c) => c.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch (err) {
      if (err.response?.status === 401) logout();
      else setMsg({ text: formatApiErrorDetail(err.response?.data?.detail) || err.message, type: "error" });
    }
  };

  const exportXlsx = async () => {
    try {
      const res = await api.get("/admin/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `checkin-anfragen-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 401) logout();
      else setMsg({ text: "Export fehlgeschlagen.", type: "error" });
    }
  };

  const dt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE") : "—");

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const resetFilters = () => setFilters({ product: "", status: "", from: "", to: "", q: "" });
  const inRange = (iso) => {
    if (!iso) return true;
    const d = iso.slice(0, 10);
    if (filters.from && d < filters.from) return false;
    if (filters.to && d > filters.to) return false;
    return true;
  };
  const q = filters.q.trim().toLowerCase();
  const matchQ = (fields) => !q || fields.some((v) => String(v || "").toLowerCase().includes(q));
  const filteredLeads = leads.filter(
    (l) => (!filters.product || l.product === filters.product) && (!filters.status || l.status === filters.status)
      && inRange(l.created_at) && matchQ([l.name, l.email, l.phone, l.postcode])
  );
  const filteredCallbacks = callbacks.filter(
    (c) => (!filters.status || c.status === filters.status) && inRange(c.created_at) && matchQ([c.name, c.email, c.phone, c.topic])
  );

  const renderTable = (product, title, headClass) => (
    <div className="admin-table-wrap">
      <div className="admin-table-heading">
        <div><span className="kicker">{title}</span><h2>Preisspannen nach PLZ-Region</h2></div>
        <p>Die erste Ziffer der Postleitzahl dient als Region. Produktiv können feinere PLZ-Bereiche hinterlegt werden.</p>
      </div>
      <div className="table-scroll">
        <table data-testid={`table-${product}`}>
          <thead>
            <tr>
              <th>PLZ-Bereich</th>
              <th className={headClass}>ct min.</th><th className={headClass}>ct max.</th>
              <th className={headClass}>Grundpreis min.</th><th className={headClass}>Grundpreis max.</th>
              <th>Bezeichnung</th><th>Stand</th>
            </tr>
          </thead>
          <tbody>
            {byProduct(product).map((r) => {
              const e = rows[r.id] || {};
              return (
                <tr key={r.id}>
                  <td>{e.postal_code_from}–{e.postal_code_to}{e.hasDraft && <span className="draft-flag">Entwurf</span>}</td>
                  {NUM_FIELDS.map((f) => (
                    <td key={f}>
                      <input type="number" min="0" step="0.1" value={e[f] ?? ""} data-testid={`input-${r.id}-${f}`}
                        onChange={(ev) => update(r.id, f, ev.target.value)} aria-label={`${product} ${f}`} />
                    </td>
                  ))}
                  <td><input value={e.label ?? ""} onChange={(ev) => update(r.id, "label", ev.target.value)} data-testid={`input-${r.id}-label`} /></td>
                  <td><input type="date" value={e.valid_from ?? ""} onChange={(ev) => update(r.id, "valid_from", ev.target.value)} data-testid={`input-${r.id}-valid_from`} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="admin-body">
      <header className="admin-header">
        <div className="admin-shell">
          <a href="/" className="brand"><img src="/checkin-tarife-logo.png" alt="Check'in Tarife" /></a>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {me && <span style={{ color: "var(--muted)", fontSize: "0.85rem", fontWeight: 700 }} data-testid="admin-user">{me.name || me.username}</span>}
            <a href="/" className="button button-ghost button-small">← Zur Website</a>
            <button className="button button-navy button-small" type="button" onClick={logout} data-testid="admin-logout">Abmelden</button>
          </div>
        </div>
      </header>

      <main className="admin-shell admin-main">
        <section className="admin-hero">
          <div>
            <span className="kicker">Geschützter Bereich</span>
            <h1>Orientierungswerte</h1>
            <p>Monatliche Strom- und Gas-Preisspannen nach Postleitzahlbereich verwalten.</p>
          </div>
          <div className="admin-status"><span></span><div><small>Status</small><strong>Serverseitig gesichert</strong></div></div>
        </section>

        <div className="admin-tabs" data-testid="admin-tabs">
          <button className={`admin-tab${view === "orientation" ? " is-active" : ""}`} type="button"
            onClick={() => setView("orientation")} data-testid="tab-orientation">Orientierungswerte</button>
          <button className={`admin-tab${view === "leads" ? " is-active" : ""}`} type="button"
            onClick={() => setView("leads")} data-testid="tab-leads">
            Anfragen & Rückrufe <span className="tab-count">{leads.length + callbacks.length}</span>
          </button>
        </div>

        {msg.text && <p className={`admin-message ${msg.type}`} aria-live="polite" data-testid="admin-message">{msg.text}</p>}

        {view === "orientation" && (
          <>
            <aside className="prototype-warning">
              <strong>Testbetrieb:</strong> Die hinterlegten Werte sind plausible Testwerte. Änderungen werden als Entwurf gespeichert
              und wirken erst nach bewusster Veröffentlichung im öffentlichen Pre-Check. Jede Veröffentlichung wird protokolliert.
            </aside>

            <section className="admin-toolbar">
              <div style={{ color: "var(--muted)", fontSize: "0.86rem" }}>
                Bearbeiten Sie die Werte in den Tabellen und speichern Sie zunächst als Entwurf. Über „Veröffentlichen" werden alle Werte live geschaltet.
              </div>
              <div className="admin-actions">
                <button className="button button-ghost" type="button" onClick={() => save("draft")} disabled={busy} data-testid="save-draft-btn">Entwurf speichern</button>
                <button className="button button-coral" type="button" onClick={() => save("publish")} disabled={busy} data-testid="publish-btn">Veröffentlichen</button>
              </div>
            </section>

            {renderTable("electricity", "Strom", "strom-head")}
            {renderTable("gas", "Gas", "gas-head")}

            <div className="admin-table-wrap">
              <div className="admin-table-heading">
                <div><span className="kicker">Nachvollziehbarkeit</span><h2>Änderungsprotokoll</h2></div>
                <p>Letzte Veröffentlichungen mit Administrator, Zeitpunkt sowie altem und neuem Wert.</p>
              </div>
              {audit.length === 0 ? (
                <p style={{ color: "var(--muted)" }} data-testid="audit-empty">Noch keine Veröffentlichungen protokolliert.</p>
              ) : (
                <div className="audit-list" data-testid="audit-list">
                  {audit.map((a) => (
                    <div className="audit-item" key={a.id}>
                      <strong>{a.product === "electricity" ? "Strom" : "Gas"} · PLZ {a.region_prefix}xxxx</strong>{" "}
                      <small>{dt(a.changed_at)} · {a.admin_username || a.admin_email}</small>
                      <div style={{ marginTop: 6, fontSize: "0.8rem", color: "var(--muted)" }}>
                        Arbeitspreis: {a.before_values?.work_price_low}–{a.before_values?.work_price_high} →{" "}
                        <strong style={{ color: "var(--ink)" }}>{a.after_values?.work_price_low}–{a.after_values?.work_price_high}</strong> ct/kWh
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {view === "leads" && (
          <>
            <section className="admin-toolbar">
              <div style={{ color: "var(--muted)", fontSize: "0.86rem" }}>
                Alle eingegangenen Pre-Check-Anfragen und Rückrufe. Status pflegen und als Excel exportieren.
              </div>
              <div className="admin-actions">
                <button className="button button-coral" type="button" onClick={exportXlsx} data-testid="export-xlsx-btn">Excel exportieren</button>
              </div>
            </section>

            <section className="admin-filters" data-testid="admin-filters">
              <div className="filter-field">
                <span>Suche</span>
                <input type="text" placeholder="Name, E-Mail, Telefon, PLZ…" value={filters.q}
                  onChange={(e) => setFilter("q", e.target.value)} data-testid="filter-search" />
              </div>
              <div className="filter-field">
                <span>Produkt</span>
                <select value={filters.product} onChange={(e) => setFilter("product", e.target.value)} data-testid="filter-product">
                  <option value="">Alle</option><option value="strom">Strom</option><option value="gas">Gas</option>
                  <option value="internet">Internet</option><option value="mobilfunk">Mobilfunk</option>
                </select>
              </div>
              <div className="filter-field">
                <span>Status</span>
                <select value={filters.status} onChange={(e) => setFilter("status", e.target.value)} data-testid="filter-status">
                  <option value="">Alle</option>
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
              </div>
              <div className="filter-field">
                <span>Von</span>
                <input type="date" value={filters.from} onChange={(e) => setFilter("from", e.target.value)} data-testid="filter-from" />
              </div>
              <div className="filter-field">
                <span>Bis</span>
                <input type="date" value={filters.to} onChange={(e) => setFilter("to", e.target.value)} data-testid="filter-to" />
              </div>
              <button className="button button-ghost button-small" type="button" onClick={resetFilters} data-testid="filter-reset">Zurücksetzen</button>
            </section>

            <div className="admin-table-wrap">
              <div className="admin-table-heading">
                <div><span className="kicker">Leads</span><h2>Pre-Check-Anfragen <span className="tab-count">{filteredLeads.length}/{leads.length}</span></h2></div>
                <p>Ergebnis-Ampel, Kontaktdaten und Bearbeitungsstatus je Anfrage.</p>
              </div>
              <div className="table-scroll">
                <table data-testid="leads-table" style={{ minWidth: 1100 }}>
                  <thead>
                    <tr><th>Datum</th><th>Produkt</th><th>Ampel</th><th>Name</th><th>Telefon</th><th>E-Mail</th><th>PLZ</th><th>Kontaktzeit</th><th>E-Mail</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {filteredLeads.length === 0 ? (
                      <tr><td colSpan={10} style={{ color: "var(--muted)", fontWeight: 400 }} data-testid="leads-empty">Keine Anfragen gefunden.</td></tr>
                    ) : filteredLeads.map((l) => (
                      <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                        <td style={{ fontWeight: 400, fontSize: "0.8rem" }}>{dt(l.created_at)}</td>
                        <td style={{ fontSize: "0.86rem" }}>{l.product}</td>
                        <td><span className={`status-dot-lg lvl-${l.result_level}`} title={l.result_level}></span></td>
                        <td style={{ fontSize: "0.86rem" }}>{l.name}</td>
                        <td style={{ fontWeight: 400, fontSize: "0.82rem" }}>{l.phone}</td>
                        <td style={{ fontWeight: 400, fontSize: "0.82rem" }}>{l.email}</td>
                        <td style={{ fontWeight: 400 }}>{l.postcode}</td>
                        <td style={{ fontWeight: 400, fontSize: "0.8rem" }}>{l.preferred_contact_time}</td>
                        <td style={{ fontWeight: 400 }}>{l.email_sent ? "✓" : "—"}</td>
                        <td>
                          <select className={`status-select st-${l.status}`} value={l.status}
                            onChange={(e) => changeStatus("leads", l.id, e.target.value)} data-testid={`lead-status-${l.id}`}>
                            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="admin-table-wrap">
              <div className="admin-table-heading">
                <div><span className="kicker">Rückrufe</span><h2>Rückruf-Anfragen <span className="tab-count">{filteredCallbacks.length}/{callbacks.length}</span></h2></div>
                <p>Über das Kontaktformular eingegangene Rückruf-Wünsche.</p>
              </div>
              <div className="table-scroll">
                <table data-testid="callbacks-table" style={{ minWidth: 900 }}>
                  <thead>
                    <tr><th>Datum</th><th>Name</th><th>Telefon</th><th>E-Mail</th><th>Anliegen</th><th>Kontaktzeit</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {filteredCallbacks.length === 0 ? (
                      <tr><td colSpan={7} style={{ color: "var(--muted)", fontWeight: 400 }} data-testid="callbacks-empty">Keine Rückruf-Anfragen gefunden.</td></tr>
                    ) : filteredCallbacks.map((c) => (
                      <tr key={c.id} data-testid={`callback-row-${c.id}`}>
                        <td style={{ fontWeight: 400, fontSize: "0.8rem" }}>{dt(c.created_at)}</td>
                        <td style={{ fontSize: "0.86rem" }}>{c.name}</td>
                        <td style={{ fontWeight: 400, fontSize: "0.82rem" }}>{c.phone}</td>
                        <td style={{ fontWeight: 400, fontSize: "0.82rem" }}>{c.email || "—"}</td>
                        <td style={{ fontWeight: 400 }}>{c.topic || "—"}</td>
                        <td style={{ fontWeight: 400, fontSize: "0.8rem" }}>{c.preferred_time}</td>
                        <td>
                          <select className={`status-select st-${c.status}`} value={c.status}
                            onChange={(e) => changeStatus("callbacks", c.id, e.target.value)} data-testid={`callback-status-${c.id}`}>
                            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
